import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Col, Form, ListGroup, Row, Spinner, Table } from 'react-bootstrap';
import { FaMinus, FaPlus, FaTimes } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import ElegantModal from '../../components/ElegantModal/ElegantModal';
import { urlApiProd } from '../utiles';
import { parseNum, roundN } from './inventarioCalculos';
import { medidaFaturamentoStr } from './inventarioOcUtils';
import { emitirPackingListExcel } from './emitirPackingListExcel';
import { recalcularLineaPacking } from './packingListCalculos';
import { detalleLineasPackingModificado } from './packingListDetalleLineas';
import {
  contenedorEnDraftKey,
  draftDeLinea,
  maxCorrelativoEnDraft,
  normalizeDraftLabel,
  validarContenedoresConsecutivosPorDraft,
} from './packingListDraftContenedores';

/** Columna precio intercompany por línea (PL logístico /packinglist). */
const PL_COL_LABEL_USD_M3_ICB = 'USD/M³ (ICB)';

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function foldBusquedaTexto(s) {
  return String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function proformaEtiqueta(pi, clientes = []) {
  if (!pi) return '';
  const idPi = pi.id_pi != null ? String(pi.id_pi).trim() : '';
  if (idPi) return idPi;
  const cliente = (clientes || []).find((c) => String(c.id_cliente) === String(pi.cliente));
  const siglas = String(cliente?.siglas || '').trim();
  const corrNum = pi.correlativo != null ? parseInt(pi.correlativo, 10) : NaN;
  const corr = Number.isFinite(corrNum) && corrNum >= 0 ? String(corrNum).padStart(2, '0') : '';
  const year = pi.fecha_embarque ? new Date(pi.fecha_embarque).getUTCFullYear() : null;
  const yy = Number.isFinite(year) ? String(year).slice(-2) : '';
  if (siglas && corr && yy) return `${siglas} ${corr}-${yy}`;
  if (corr) return siglas ? `${siglas} ${corr}` : corr;
  if (pi.id_proforma) return `PI ${pi.id_proforma}`;
  return '';
}

function proformaBusquedaHaystack(pi, clientes = []) {
  if (!pi) return '';
  const lbl = proformaEtiqueta(pi, clientes);
  const idn = pi.id_proforma != null ? String(pi.id_proforma) : '';
  const corr = pi.correlativo != null ? String(pi.correlativo) : '';
  return `${lbl} ${idn} ${corr}`;
}

function normalizeDim(v) {
  if (v == null || v === '') return '';
  const n = parseNum(v);
  if (n == null) return String(v).trim().toLowerCase().replace(',', '.');
  return String(n);
}

/** Partes de una medida "e x a x l" normalizadas para comparar. */
function dimPartsFromSizeStr(raw) {
  return String(raw ?? '')
    .toLowerCase()
    .replace(/,/g, '.')
    .split(/[x×]/i)
    .map((p) => normalizeDim(p.trim()))
    .filter(Boolean);
}

/**
 * Medidas de factura (compra) de los productos de la PI.
 * Es la misma base que usan las NF de inventario (no real_size).
 */
export function medidasFacturaFromPi(pi, productos = [], medidas = []) {
  if (!pi) return [];
  const ids = Array.isArray(pi.productos) ? pi.productos : [];
  const out = [];
  const seen = new Set();
  ids.forEach((pid) => {
    const prod = (productos || []).find((p) => Number(p.id_producto) === Number(pid));
    const str = medidaFaturamentoStr(prod, medidas);
    if (!str) return;
    const key = dimPartsFromSizeStr(str).join('x');
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(str);
  });
  return out;
}

/** Valida si las medidas de la línea NF existen en la medida de factura de la PI. */
export function piAceptaMedida(pi, linea, catalog = {}) {
  if (!pi) return false;
  const { productos = [], medidas = [] } = catalog;
  const sizes = medidasFacturaFromPi(pi, productos, medidas);
  // Sin catálogo / sin productos resolubles: no bloquear (evita falsos negativos).
  if (!sizes.length) return true;
  const esp = normalizeDim(linea.espesor);
  const anc = normalizeDim(linea.ancho);
  const lar = normalizeDim(linea.largo);
  if (!esp && !anc && !lar) return true;
  return sizes.some((raw) => {
    const parts = dimPartsFromSizeStr(raw);
    if (parts.length < 2) return false;
    const [e, a, l] = parts;
    const okE = !esp || !e || e === esp;
    const okA = !anc || !a || a === anc;
    const okL = !lar || !l || l === lar;
    return okE && okA && okL;
  });
}

export function piAceptaProducto(pi, linea) {
  if (!pi) return false;
  const idProducto = Number(linea?.id_producto);
  if (!Number.isFinite(idProducto)) return true;
  const ids = Array.isArray(pi.productos)
    ? pi.productos.map((id) => Number(id)).filter(Number.isFinite)
    : [];
  // Sin productos resolubles en PI: no bloquear para evitar falsos negativos legacy.
  if (!ids.length) return true;
  return ids.includes(idProducto);
}

export function piCompatibleConLinea(pi, linea, catalog = {}) {
  return piAceptaProducto(pi, linea) && piAceptaMedida(pi, linea, catalog);
}

function piCompatibleConAlgunaLinea(pi, lineas = [], catalog = {}) {
  if (!lineas.length) return true;
  return lineas.some((ln) => piCompatibleConLinea(pi, ln, catalog));
}

function draftOptions(cantidad) {
  const n = Math.max(1, Math.min(20, Number(cantidad) || 1));
  return Array.from({ length: n }, (_, i) => String(i + 1));
}

function correlativoContenedorNum(linea) {
  return Number(linea?.correlativo_contenedor) || 1;
}

function Stepper({ value, onChange, min = 0 }) {
  const v = Number(value) || 0;
  return (
    <div className="d-inline-flex align-items-center gap-0 inv-pl-stepper">
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm py-0 px-1"
        onClick={() => onChange(Math.max(min, v - 1))}
        title="Restar"
      >
        <FaMinus size={9} />
      </button>
      <span className="px-1 inv-pl-stepper-val">{v}</span>
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm py-0 px-1"
        onClick={() => onChange(v + 1)}
        title="Sumar"
      >
        <FaPlus size={9} />
      </button>
    </div>
  );
}

function TruncCell({ children, title, className = '' }) {
  const text = children == null || children === '' ? '' : String(children);
  return (
    <span className={`inv-pl-trunc ${className}`} title={title || text}>
      {text || '—'}
    </span>
  );
}

function formatClNum(v, { min = 2, max = 2 } = {}) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseNum(v);
  if (n == null || Number.isNaN(n)) return null;
  return n.toLocaleString('es-CL', {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });
}

function formatEspesorDisplay(v) {
  return formatClNum(v, { min: 0, max: 3 }) ?? '—';
}

function formatMilesDisplay(v, fallback = '—') {
  return formatClNum(v, { min: 0, max: 2 }) ?? fallback;
}

function formatM3Display(v, fallback = '') {
  return formatClNum(v, { min: 3, max: 3 }) ?? fallback;
}

function sumM3Values(values) {
  return (values || []).reduce((acc, raw) => {
    const n = parseNum(raw);
    return n == null ? acc : acc + n;
  }, 0);
}

function formatMoneyDisplay(v) {
  return formatClNum(v, { min: 2, max: 2 }) ?? '—';
}

function totalUsdGanancia(linea) {
  const precio = parseNum(linea.precio_usd_m3);
  const m3 = parseNum(linea.total_m3_ganancia);
  return precio != null && m3 != null ? roundN(precio * m3, 2) : null;
}

function gradoPrecioKey(grado) {
  return String(grado || '').trim().toUpperCase();
}

function formatPesoDisplay(v) {
  return formatMilesDisplay(v, '');
}

function numberToEditString(v) {
  if (v == null || v === '') return '';
  const n = typeof v === 'number' ? v : parseNum(v);
  if (n == null) return String(v);
  const [ent, dec] = String(n).split('.');
  return dec != null ? `${ent},${dec}` : ent;
}

function parseLocalizedInput(s) {
  if (s == null || s === '') return null;
  const raw = String(s).trim();
  if (raw.includes(',')) return parseNum(raw.replace(/\./g, ''));
  if (/^\d{1,3}(\.\d{3})+$/.test(raw)) return parseNum(raw.replace(/\./g, ''));
  return parseNum(raw);
}

function FormattedNumberInput({ value, onChange, className = '', placeholder = '0', formatter = formatPesoDisplay }) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');

  const display = focused ? draft : formatter(value);

  return (
    <Form.Control
      size="sm"
      className={className}
      value={display}
      placeholder={placeholder}
      inputMode="decimal"
      onFocus={() => {
        setDraft(numberToEditString(value));
        setFocused(true);
      }}
      onChange={(e) => {
        let s = String(e.target.value).replace(/[^\d.,]/g, '');
        const ci = s.indexOf(',');
        if (ci >= 0) {
          s = s.slice(0, ci + 1) + s.slice(ci + 1).replace(/[.,]/g, '');
        }
        const di = s.indexOf('.');
        if (di >= 0 && ci < 0) {
          s = s.slice(0, di + 1) + s.slice(di + 1).replace(/[.,]/g, '');
        }
        setDraft(s);
        onChange(parseLocalizedInput(s));
      }}
      onBlur={() => {
        onChange(parseLocalizedInput(draft));
        setFocused(false);
      }}
    />
  );
}

function nombreDesdeForm({ booking, modoDraft, version }) {
  let base = (booking || '').trim() || 'PackingList';
  if (version > 1) base = `${base} V${version}`;
  return base;
}

export function prepararPayloadNuevaVersion(payload, origenPlId) {
  const nuevaVersion = { ...payload, origen_pl_id: origenPlId };
  // El backend es la fuente de verdad para ambos valores correlacionados.
  delete nuevaVersion.version;
  delete nuevaVersion.nombre;
  return nuevaVersion;
}

function refrescarComposicionDesdeInventario(linea, notasDisponibles = []) {
  const nf = (notasDisponibles || []).find(
    (nota) => Number(nota.id) === Number(linea.id_inventario_nf)
  );
  const source = (nf?.lineas || []).find(
    (item) => Number(item.id) === Number(linea.id_inventario_linea)
  );
  if (!source) return linea;
  const paquetes = parseNum(linea.paquetes) ?? 0;
  const paquetesOrigen = parseNum(source.paquetes);
  const totalM3Original = parseNum(source.total_m3);
  const totalM3 = paquetesOrigen > 0 && totalM3Original != null
    ? roundN((totalM3Original * paquetes) / paquetesOrigen, 6)
    : parseNum(linea.total_m3);
  return {
    ...linea,
    grado: source.grado,
    castigo_rw: source.castigo_rw,
    altura_rw: source.altura_rw,
    paquetes_origen: source.paquetes,
    pcs_paq: source.pcs_paq,
    total_pcs: paquetes * (parseNum(source.pcs_paq) || 0),
    espesor: source.espesor,
    ancho: source.ancho,
    largo: source.largo,
    total_m3: totalM3,
    total_m3_original: source.total_m3,
    espesor_ganancia: source.espesor,
    ancho_ganancia: source.ancho,
    largo_ganancia: source.largo,
    castigo_rw_ganancia: source.castigo_rw,
    total_m3_ganancia: totalM3,
  };
}

/**
 * @param {object} props
 * @param {boolean} props.show
 * @param {() => void} props.onHide
 * @param {Array} props.seleccionInicial - filas/notas seleccionadas [{id_inventario_nf, id_inventario_linea?}]
 * @param {object|null} props.packingExistente - para editar
 * @param {() => void} props.onSaved
 * @param {Array} props.notasDisponibles - NFs guardadas para agregar dentro del form
 */
export default function CrearPackingListDialog({
  show,
  onHide,
  seleccionInicial = [],
  packingExistente = null,
  onSaved,
  notasDisponibles = [],
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState(null); // 'borrador' | 'excel'
  const [error, setError] = useState('');
  const [proformas, setProformas] = useState([]);
  const [saldosPi, setSaldosPi] = useState({});
  const [loadingSaldosPi, setLoadingSaldosPi] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [medidas, setMedidas] = useState([]);
  const [piBusqueda, setPiBusqueda] = useState('');
  const [piMenuOpen, setPiMenuOpen] = useState(false);
  const [idsProforma, setIdsProforma] = useState([]);
  const [booking, setBooking] = useState('');
  const [conGanancia, setConGanancia] = useState(false);
  const [sinGanancia, setSinGanancia] = useState(true);
  const [modoDraft, setModoDraft] = useState('uno');
  const [cantidadDrafts, setCantidadDrafts] = useState(2);
  const [version, setVersion] = useState(1);
  /** Estado del PL abierto; una emisión desde uno vigente o en revisión crea otra versión. */
  const [estadoOriginal, setEstadoOriginal] = useState('borrador');
  const [lineas, setLineas] = useState([]);
  const [nfBusqueda, setNfBusqueda] = useState('');
  const [nfMenuOpen, setNfMenuOpen] = useState(false);
  const [addingNf, setAddingNf] = useState(false);
  const [nfAEliminar, setNfAEliminar] = useState(null);
  /** Líneas que han estado en este PL en el dialog; si se quitan, pueden re-agregarse. */
  const lineasDelPlSesionRef = useRef(new Set());
  const draftsEditadosRef = useRef(new Set());
  /** Detalle de líneas al abrir el PL (para no crear versión al reemitir Excel sin cambios). */
  const lineasInicialesRef = useRef(null);
  const [plId, setPlId] = useState(null);
  const [origenPlId, setOrigenPlId] = useState(null);
  const [redirectOpen, setRedirectOpen] = useState(false);
  const [redirectSec, setRedirectSec] = useState(5);
  const redirectOpenRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirigirTrasGuardar = location.pathname === '/inventario';
  const piInputRef = useRef(null);
  const piElegidaRecienteRef = useRef(false);

  const nombre = useMemo(
    () => nombreDesdeForm({ booking, modoDraft, version }),
    [booking, modoDraft, version]
  );

  const drafts = useMemo(
    () => draftOptions(modoDraft === 'varios' ? cantidadDrafts : 1),
    [modoDraft, cantidadDrafts]
  );

  const soloLectura = useMemo(() => {
    const e = String(packingExistente?.estado || estadoOriginal || '').toLowerCase();
    return e === 'historico';
  }, [packingExistente?.estado, estadoOriginal]);

  const draftEdicionActivo = useMemo(() => {
    const varios =
      modoDraft === 'varios' ||
      String(packingExistente?.modo_draft || '').toLowerCase() === 'varios';
    if (!varios) return '';
    return normalizeDraftLabel(packingExistente?.draft_label) || '';
  }, [modoDraft, packingExistente]);

  const lineasTabla = useMemo(() => {
    const defaultDraft = drafts[0] || '1';
    const rows = lineas.map((ln, idx) => ({ ln, idx }));
    if (!draftEdicionActivo) return rows;
    return rows.filter(({ ln }) => draftDeLinea(ln, defaultDraft) === draftEdicionActivo);
  }, [lineas, draftEdicionActivo, drafts]);

  const proformasSel = useMemo(() => {
    const set = new Set(idsProforma.map(String));
    return proformas.filter((p) => set.has(String(p.id_proforma)));
  }, [proformas, idsProforma]);

  const catalogoMedidasPi = useMemo(
    () => ({ productos, medidas }),
    [productos, medidas]
  );

  const markDraftEditado = useCallback((draftLabel) => {
    if (!packingExistente?.id) return;
    const draft = normalizeDraftLabel(draftLabel);
    if (draft) draftsEditadosRef.current.add(draft);
  }, [packingExistente]);

  const markLineaDraftEditado = useCallback((linea) => {
    markDraftEditado(linea?.draft_label);
  }, [markDraftEditado]);

  const lineasConPiUnica = useCallback(
    (rows) => {
      if (idsProforma.length !== 1) return rows;
      const only = Number(idsProforma[0]);
      const pi = proformas.find((p) => String(p.id_proforma) === String(only));
      return rows.map((l) =>
        l.id_proforma || !pi || !piCompatibleConLinea(pi, l, catalogoMedidasPi)
          ? l
          : { ...l, id_proforma: only }
      );
    },
    [idsProforma, proformas, catalogoMedidasPi]
  );

  const volumenActualPorPi = useMemo(() => {
    const out = {};
    for (const ln of lineasConPiUnica(lineas)) {
      if (!ln.id_proforma) continue;
      const id = String(ln.id_proforma);
      const m3 = parseNum(ln.total_m3);
      if (m3 == null) continue;
      out[id] = (out[id] || 0) + m3;
    }
    return out;
  }, [lineas, lineasConPiUnica]);

  const balancesPi = useMemo(
    () =>
      proformasSel.map((pi) => {
        const id = String(pi.id_proforma);
        const saldo = saldosPi[id] || {};
        const contratado =
          parseNum(saldo.volumen_contratado) ?? sumM3Values(Array.isArray(pi.m3) ? pi.m3 : []);
        const emitido = parseNum(saldo.volumen_emitido) ?? 0;
        const borrador = parseNum(saldo.volumen_borrador) ?? 0;
        const utilizado = parseNum(saldo.volumen_utilizado) ?? emitido + borrador;
        const actual = volumenActualPorPi[id] || 0;
        const saldoDisponible = contratado - utilizado;
        const saldoPosterior = saldoDisponible - actual;
        return {
          id,
          etiqueta: proformaEtiqueta(pi, clientes),
          contratado,
          emitido,
          borrador,
          utilizado,
          actual,
          saldoDisponible,
          saldoPosterior,
          excedido: contratado > 0 && saldoPosterior < -0.0005,
        };
      }),
    [proformasSel, saldosPi, volumenActualPorPi, clientes]
  );

  const proformasFiltradas = useMemo(() => {
    const qf = foldBusquedaTexto(piBusqueda);
    const ya = new Set(idsProforma.map(String));
    return proformas
      .filter((p) => !ya.has(String(p.id_proforma)))
      .filter((p) => piCompatibleConAlgunaLinea(p, lineas, catalogoMedidasPi))
      .filter((p) => {
        if (!qf) return true;
        return foldBusquedaTexto(proformaBusquedaHaystack(p, clientes)).includes(qf);
      })
      .sort((a, b) => {
        const ca = Number(a.correlativo);
        const cb = Number(b.correlativo);
        if (Number.isFinite(ca) && Number.isFinite(cb) && cb !== ca) return cb - ca;
        if (Number.isFinite(cb) && !Number.isFinite(ca)) return 1;
        if (Number.isFinite(ca) && !Number.isFinite(cb)) return -1;
        return (Number(b.id_proforma) || 0) - (Number(a.id_proforma) || 0);
      })
      .slice(0, 40);
  }, [proformas, piBusqueda, idsProforma, clientes, lineas, catalogoMedidasPi]);

  const loadProformas = useCallback(async () => {
    const res = await fetch(`${urlApiProd}/proformas`, { headers: authHeaders() });
    if (!res.ok) throw new Error('No se pudieron cargar proformas');
    const data = await res.json();
    setProformas(Array.isArray(data) ? data : []);
  }, []);

  const loadClientes = useCallback(async () => {
    const res = await fetch(`${urlApiProd}/clientes`, { headers: authHeaders() });
    if (!res.ok) throw new Error('No se pudieron cargar clientes');
    const data = await res.json();
    setClientes(Array.isArray(data) ? data : []);
  }, []);

  const loadCatalogoMedidas = useCallback(async () => {
    const [rProd, rMed] = await Promise.all([
      fetch(`${urlApiProd}/productos`, { headers: authHeaders() }),
      fetch(`${urlApiProd}/medidas`, { headers: authHeaders() }),
    ]);
    if (rProd.ok) {
      const data = await rProd.json();
      setProductos(Array.isArray(data) ? data : []);
    }
    if (rMed.ok) {
      const data = await rMed.json();
      setMedidas(Array.isArray(data) ? data : []);
    }
  }, []);

  const loadPreview = useCallback(async (seleccion, allowLineIds = []) => {
    const res = await fetch(`${urlApiProd}/inventario/packinglists/preview-lineas`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        seleccion,
        precio_icb: null,
        allow_line_ids: allowLineIds,
      }),
    });
    if (!res.ok) throw new Error('No se pudieron armar las líneas');
    const data = await res.json();
    return data.lineas || [];
  }, []);

  useEffect(() => {
    if (!show || !idsProforma.length) {
      setSaldosPi({});
      setLoadingSaldosPi(false);
      return undefined;
    }
    let cancelled = false;
    const params = new URLSearchParams({ ids: idsProforma.join(',') });
    const excluir = [...new Set([plId, origenPlId].filter(Boolean).map(String))];
    if (excluir.length) params.set('exclude_pl_ids', excluir.join(','));
    setLoadingSaldosPi(true);
    fetch(`${urlApiProd}/inventario/packinglists/proforma-saldos?${params.toString()}`, {
      headers: authHeaders(),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'No se pudieron cargar saldos de PI');
        if (cancelled) return;
        const byId = {};
        for (const row of data.saldos || []) {
          byId[String(row.id_proforma)] = row;
        }
        setSaldosPi(byId);
      })
      .catch(() => {
        if (!cancelled) setSaldosPi({});
      })
      .finally(() => {
        if (!cancelled) setLoadingSaldosPi(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, idsProforma, plId, origenPlId]);

  useEffect(() => {
    if (!lineas.length) return;
    const pis = new Map(proformasSel.map((pi) => [String(pi.id_proforma), pi]));
    setLineas((prev) => {
      let changed = false;
      const next = prev.map((ln) => {
        if (!ln.id_proforma) return ln;
        const pi = pis.get(String(ln.id_proforma));
        if (pi && piCompatibleConLinea(pi, ln, catalogoMedidasPi)) return ln;
        changed = true;
        return { ...ln, id_proforma: null };
      });
      return changed ? next : prev;
    });
  }, [proformasSel, catalogoMedidasPi, lineas.length]);

  useEffect(() => {
    if (!show) {
      redirectOpenRef.current = false;
      setRedirectOpen(false);
      setRedirectSec(5);
      return;
    }
    if (redirectOpenRef.current) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setNfBusqueda('');
      setNfMenuOpen(false);
      setAddingNf(false);
      setNfAEliminar(null);
      lineasDelPlSesionRef.current = new Set();
      draftsEditadosRef.current = new Set();
      lineasInicialesRef.current = null;
      try {
        await Promise.all([loadProformas(), loadClientes(), loadCatalogoMedidas()]);
        if (packingExistente?.id) {
          const res = await fetch(`${urlApiProd}/inventario/packinglists/${packingExistente.id}`, {
            headers: authHeaders(),
          });
          if (!res.ok) throw new Error('No se pudo cargar el packing list');
          const pl = await res.json();
          if (cancelled) return;
          setPlId(pl.id);
          setOrigenPlId(pl.id_origen_pl ?? null);
          setBooking(pl.booking || '');
          setConGanancia(!!pl.con_ganancia);
          setSinGanancia(!!pl.sin_ganancia);
          setModoDraft(pl.modo_draft || 'uno');
          setCantidadDrafts(pl.cantidad_drafts || 2);
          setVersion(pl.version || 1);
          setEstadoOriginal(pl.estado || 'borrador');
          setIdsProforma((pl.proformas || []).map((p) => String(p.id_proforma)));
          const plLineas = pl.lineas || [];
          plLineas.forEach((l) => {
            if (l.id_inventario_linea != null) {
              lineasDelPlSesionRef.current.add(String(l.id_inventario_linea));
            }
          });
          const lineasCargadas = plLineas.map((l) => {
            const composicion = String(pl.estado || '').toLowerCase() === 'revision'
              ? refrescarComposicionDesdeInventario(l, notasDisponibles)
              : l;
            return {
              ...composicion,
              espesor_ganancia: composicion.espesor_ganancia ?? composicion.espesor,
              ancho_ganancia: composicion.ancho_ganancia ?? composicion.ancho,
              largo_ganancia: composicion.largo_ganancia ?? composicion.largo,
              castigo_rw_ganancia: composicion.castigo_rw_ganancia ?? composicion.castigo_rw,
              draft_label: normalizeDraftLabel(composicion.draft_label) || composicion.draft_label,
            };
          });
          setLineas(lineasCargadas);
          lineasInicialesRef.current = lineasCargadas.map((ln) => ({ ...ln }));
        } else {
          setPlId(null);
          setOrigenPlId(null);
          setBooking('');
          setConGanancia(false);
          setSinGanancia(true);
          setModoDraft('uno');
          setCantidadDrafts(2);
          setVersion(1);
          setEstadoOriginal('borrador');
          setIdsProforma([]);
          const preview = await loadPreview(seleccionInicial);
          if (cancelled) return;
          preview.forEach((l) => {
            if (l.id_inventario_linea != null) {
              lineasDelPlSesionRef.current.add(String(l.id_inventario_linea));
            }
          });
          const previewLineas = preview.map((l) => ({
            ...l,
            draft_label: normalizeDraftLabel(l.draft_label) || l.draft_label,
          }));
          setLineas(previewLineas);
          lineasInicialesRef.current = previewLineas.map((ln) => ({ ...ln }));
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Error al cargar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, packingExistente, seleccionInicial, notasDisponibles, loadProformas, loadClientes, loadCatalogoMedidas, loadPreview]);

  useEffect(() => {
    if (!redirectOpen) return undefined;
    if (redirectSec <= 0) {
      redirectOpenRef.current = false;
      navigate('/packinglist');
      setRedirectOpen(false);
      onHide();
      return undefined;
    }
    const t = setTimeout(() => setRedirectSec((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [redirectOpen, redirectSec, navigate, onHide]);

  const cancelRedirect = () => {
    redirectOpenRef.current = false;
    setRedirectOpen(false);
    setRedirectSec(5);
    onHide();
  };

  const abrirRedirectPackingList = () => {
    redirectOpenRef.current = true;
    setRedirectSec(5);
    setRedirectOpen(true);
  };

  useEffect(() => {
    if (!lineas.length) return;
    const allowed = new Set(drafts);
    setLineas((prev) => {
      let changed = false;
      const next = prev.map((ln) => {
        const current = normalizeDraftLabel(ln.draft_label);
        const draft = modoDraft === 'varios' && allowed.has(current) ? current : drafts[0];
        if (draft === ln.draft_label) return ln;
        changed = true;
        return { ...ln, draft_label: draft };
      });
      return changed ? next : prev;
    });
  }, [modoDraft, drafts, lineas.length]);

  const patchLinea = (idx, patch) => {
    if (soloLectura) return;
    setLineas((prev) => {
      const next = [...prev];
      const original = next[idx];
      if (!original) return prev;
      markLineaDraftEditado(original);
      let ln = { ...original, ...patch };

      if (patch.draft_label !== undefined) {
        const draft = normalizeDraftLabel(patch.draft_label) || drafts[0];
        markDraftEditado(draft);
        next[idx] = { ...original, draft_label: draft };
        return next;
      }

      if (patch.correlativo_contenedor !== undefined) {
        const nuevoCont = correlativoContenedorNum(ln);
        markLineaDraftEditado(original);
        next[idx] = { ...original, correlativo_contenedor: nuevoCont };
        return next;
      }

      if (patch.id_proforma !== undefined && patch.id_proforma) {
        const pi = proformas.find((p) => String(p.id_proforma) === String(patch.id_proforma));
        if (pi && !piCompatibleConLinea(pi, ln, catalogoMedidasPi)) {
          setError(
            `La PI ${proformaEtiqueta(pi, clientes)} no es compatible con el producto/medida ${ln.espesor}x${ln.ancho}x${ln.largo} de la NF ${ln.nro_nf}.`
          );
          return prev;
        }
      }

      ln = recalcularLineaPacking(ln, patch, original);

      next[idx] = ln;
      return next;
    });
  };

  const patchPrecioIcbPorGrado = (idx, value) => {
    if (soloLectura) return;
    setLineas((prev) => {
      const base = prev[idx];
      if (!base) return prev;
      const key = gradoPrecioKey(base.grado);
      const precio = parseNum(value);
      return prev.map((ln, i) => {
        if (i !== idx && gradoPrecioKey(ln.grado) !== key) return ln;
        markLineaDraftEditado(ln);
        const m3 = parseNum(ln.total_m3);
        return {
          ...ln,
          precio_usd_m3: value,
          total_usd: precio != null && m3 != null ? roundN(precio * m3, 2) : null,
        };
      });
    });
  };

  const addProforma = (pi) => {
    if (soloLectura) return;
    if (!piCompatibleConAlgunaLinea(pi, lineas, catalogoMedidasPi)) {
      setError(`La PI ${proformaEtiqueta(pi, clientes)} no es compatible con las NF seleccionadas.`);
      return;
    }
    setIdsProforma((prev) => {
      const id = String(pi.id_proforma);
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
    setPiBusqueda('');
    setPiMenuOpen(false);
    // Si solo hay una PI, asignarla a todas las filas compatibles
    setLineas((prev) => {
      if (prev.every((l) => l.id_proforma)) return prev;
      return prev.map((ln) => {
        if (ln.id_proforma) return ln;
        if (!piCompatibleConLinea(pi, ln, catalogoMedidasPi)) return ln;
        markLineaDraftEditado(ln);
        return { ...ln, id_proforma: pi.id_proforma };
      });
    });
  };

  const removeProforma = (id) => {
    if (soloLectura) return;
    setIdsProforma((prev) => prev.filter((x) => String(x) !== String(id)));
    setLineas((prev) =>
      prev.map((ln) => {
        if (String(ln.id_proforma) !== String(id)) return ln;
        markLineaDraftEditado(ln);
        return { ...ln, id_proforma: null };
      })
    );
  };

  const onPiBusquedaChange = (e) => {
    setPiBusqueda(e.target.value);
    setPiMenuOpen(true);
  };

  const onPiInputBlur = () => {
    window.setTimeout(() => {
      if (piElegidaRecienteRef.current) {
        setPiMenuOpen(true);
        return;
      }
      setPiMenuOpen(false);
    }, 180);
  };

  const nfsParaAgregar = useMemo(() => {
    const lineasYaEnPl = new Set(
      lineas.map((l) => l.id_inventario_linea).filter((id) => id != null).map(String)
    );
    const qf = foldBusquedaTexto(nfBusqueda);
    const out = [];
    for (const nota of notasDisponibles || []) {
      if (!nota?.id) continue;
      const proveedor = nota.nombre_proveedor || nota.proveedor || '';
      for (const ln of nota.lineas || []) {
        if (!ln?.id) continue;
        const idStr = String(ln.id);
        if (lineasYaEnPl.has(idStr)) continue;
        const estadoLn = String(ln.estado_mercaderia || nota.estado_mercaderia || 'Disponible');
        // En Proceso: solo reaparece si perteneció a este PL y se quitó del listado
        if (
          (estadoLn === 'En proceso' || estadoLn === 'Borrador') &&
          !lineasDelPlSesionRef.current.has(idStr)
        ) {
          continue;
        }
        const dim = `${ln.espesor || '—'}x${ln.ancho || '—'}x${ln.largo || '—'}`;
        const label = `NF ${nota.nro_nf || '—'} · ${proveedor} · ${ln.grado || '—'} · ${dim}`;
        const haystack = foldBusquedaTexto(
          `${nota.nro_nf || ''} ${proveedor} ${nota.producto_nombre || ''} ${ln.grado || ''} ${dim} ${nota.almacen || ''}`
        );
        if (qf && !haystack.includes(qf)) continue;
        out.push({
          id_inventario_nf: nota.id,
          id_inventario_linea: ln.id,
          label,
        });
      }
    }
    return out.slice(0, 50);
  }, [notasDisponibles, lineas, nfBusqueda]);

  const agregarLineaNf = async (item) => {
    if (soloLectura) return;
    if (!item?.id_inventario_nf || !item?.id_inventario_linea) return;
    setNfMenuOpen(false);
    setNfBusqueda('');
    setAddingNf(true);
    try {
      const allow = [
        ...lineas.map((l) => l.id_inventario_linea).filter((id) => id != null),
        ...lineasDelPlSesionRef.current,
      ]
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n));
      const extra = await loadPreview(
        [{ id_inventario_nf: item.id_inventario_nf, id_inventario_linea: item.id_inventario_linea }],
        allow
      );
      if (!extra.length) {
        setError('Esa parte de la NF ya no está disponible.');
        return;
      }
      lineasDelPlSesionRef.current.add(String(item.id_inventario_linea));
      const draftNueva = draftEdicionActivo || drafts[0];
      markDraftEditado(draftNueva);
      setLineas((prev) => {
        const maxCont = maxCorrelativoEnDraft(prev, draftNueva, drafts[0]);
        const onlyPi =
          idsProforma.length === 1
            ? proformas.find((p) => String(p.id_proforma) === String(idsProforma[0]))
            : null;
        return [
          ...prev,
          ...extra.map((ln, i) => ({
            ...ln,
            correlativo_contenedor: maxCont + 1,
            draft_label: draftNueva,
            id_proforma:
              onlyPi && piCompatibleConLinea(onlyPi, ln, catalogoMedidasPi)
                ? Number(idsProforma[0])
                : ln.id_proforma,
            orden: prev.length + i + 1,
          })),
        ];
      });
    } catch (e) {
      setError(e.message || 'No se pudo agregar la línea de la NF');
    } finally {
      setAddingNf(false);
    }
  };

  const pedirEliminarNf = (ln) => {
    if (soloLectura) return;
    if (ln?.id_inventario_linea == null && ln?.id_inventario_nf == null) return;
    setNfAEliminar({
      id_inventario_nf: ln.id_inventario_nf,
      id_inventario_linea: ln.id_inventario_linea,
      draft_label: ln.draft_label,
      nro_nf: ln.nro_nf || '—',
      proveedor: ln.proveedor || '',
      grado: ln.grado || '',
      medida: `${ln.espesor || '—'}x${ln.ancho || '—'}x${ln.largo || '—'}`,
    });
  };

  const cancelarEliminarNf = () => setNfAEliminar(null);

  const confirmarEliminarNf = () => {
    if (!nfAEliminar) return;
    setLineas((prev) => {
      if (nfAEliminar.id_inventario_linea != null) {
        prev.forEach((l) => {
          if (String(l.id_inventario_linea) === String(nfAEliminar.id_inventario_linea)) {
            markLineaDraftEditado(l);
          }
        });
        return prev.filter(
          (l) => String(l.id_inventario_linea) !== String(nfAEliminar.id_inventario_linea)
        );
      }
      const idNf = String(nfAEliminar.id_inventario_nf);
      prev.forEach((l) => {
        if (String(l.id_inventario_nf) === idNf) markLineaDraftEditado(l);
      });
      return prev.filter((l) => String(l.id_inventario_nf) !== idNf);
    });
    setNfAEliminar(null);
  };

  const buildPayload = (estado) => ({
    booking: booking.trim(),
    nombre,
    precio_icb: null,
    con_ganancia: conGanancia,
    sin_ganancia: sinGanancia,
    modo_draft: modoDraft,
    cantidad_drafts: modoDraft === 'varios' ? Number(cantidadDrafts) || 2 : 1,
    version: Number(version) || 1,
    estado,
    id_proformas: idsProforma.map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n)),
    lineas: lineas.map((ln, i) => ({
      ...ln,
      orden: i + 1,
      draft_label:
        modoDraft === 'varios'
          ? normalizeDraftLabel(ln.draft_label) || drafts[0]
          : drafts[0],
    })),
  });

  const validarDrafts = (lineasFinal, { exigirTodos = false } = {}) => {
    if (modoDraft !== 'varios') return '';
    const allowed = new Set(drafts);
    for (const ln of lineasFinal) {
      const draft = draftDeLinea(ln, drafts[0]);
      const cont = correlativoContenedorNum(ln);
      if (!allowed.has(draft)) {
        return `El contenedor ${cont} (Draft ${draft}) está fuera del rango definido.`;
      }
    }
    if (exigirTodos) {
      const usados = new Set(lineasFinal.map((ln) => draftDeLinea(ln, drafts[0])));
      const faltantes = drafts.filter((d) => !usados.has(d));
      if (faltantes.length) {
        return `Asigna al menos un contenedor a cada Draft. Faltan: ${faltantes
          .map((d) => `Draft ${String(d).padStart(2, '0')}`)
          .join(', ')}.`;
      }
    }
    return '';
  };

  const persistir = async (estadoSolicitado, { emitirExcel = false } = {}) => {
    if (soloLectura) return;
    setError('');
    // Emitir Excel siempre persiste como emitido (nunca borrador)
    const estado = emitirExcel ? 'emitido' : estadoSolicitado;
    const action = emitirExcel ? 'excel' : 'borrador';

    if (!booking.trim()) {
      setError('Debe indicar el Booking.');
      return;
    }
    if (!idsProforma.length) {
      setError('Debe seleccionar al menos una Proforma Invoice (PI).');
      return;
    }
    if (!lineas.length) {
      setError('No hay líneas para el packing list.');
      return;
    }
    const sinPi = lineas.some((l) => !l.id_proforma);
    if (sinPi && idsProforma.length > 1) {
      setError('Debe asignar una PI a cada fila.');
      return;
    }
    // Asignar PI única si falta
    let lineasFinal = lineasConPiUnica(lineas);
    lineasFinal = lineasFinal.map((ln) => ({
      ...ln,
      draft_label:
        modoDraft === 'varios'
          ? normalizeDraftLabel(ln.draft_label) || drafts[0]
          : drafts[0],
    }));
    const draftError = validarDrafts(lineasFinal, { exigirTodos: emitirExcel });
    if (draftError) {
      setError(draftError);
      return;
    }
    const contError = validarContenedoresConsecutivosPorDraft(lineasFinal, {
      modoDraft,
      draftIds: drafts,
    });
    if (contError) {
      setError(contError);
      return;
    }
    for (const ln of lineasFinal) {
      const pi = proformas.find((p) => String(p.id_proforma) === String(ln.id_proforma));
      if (pi && !piCompatibleConLinea(pi, ln, catalogoMedidasPi)) {
        setError(
          `Punto de control: PI ${proformaEtiqueta(pi, clientes)} no coincide con producto/medida de NF ${ln.nro_nf} (${ln.espesor}x${ln.ancho}x${ln.largo}).`
        );
        return;
      }
    }
    const volumenFinalPorPi = {};
    for (const ln of lineasFinal) {
      if (!ln.id_proforma) continue;
      const id = String(ln.id_proforma);
      const m3 = parseNum(ln.total_m3);
      if (m3 == null) continue;
      volumenFinalPorPi[id] = (volumenFinalPorPi[id] || 0) + m3;
    }
    const piExcedida = proformasSel
      .map((pi) => {
        const id = String(pi.id_proforma);
        const saldo = saldosPi[id] || {};
        const contratado =
          parseNum(saldo.volumen_contratado) ?? sumM3Values(Array.isArray(pi.m3) ? pi.m3 : []);
        const utilizado =
          parseNum(saldo.volumen_utilizado) ??
          (parseNum(saldo.volumen_emitido) || 0) + (parseNum(saldo.volumen_borrador) || 0);
        const actual = volumenFinalPorPi[id] || 0;
        return {
          etiqueta: proformaEtiqueta(pi, clientes),
          contratado,
          utilizado,
          actual,
          saldoPosterior: contratado - utilizado - actual,
        };
      })
      .find((b) => b.contratado > 0 && b.saldoPosterior < -0.0005);
    if (piExcedida) {
      setError(
        `La PI ${piExcedida.etiqueta} supera el volumen disponible. Total: ${formatM3Display(
          piExcedida.contratado,
          '0,000'
        )} m³; ya utilizado: ${formatM3Display(piExcedida.utilizado, '0,000')} m³; este packing: ${formatM3Display(
          piExcedida.actual,
          '0,000'
        )} m³; saldo posterior: ${formatM3Display(piExcedida.saldoPosterior, '0,000')} m³.`
      );
      return;
    }

    setSaving(true);
    setSavingAction(action);
    try {
      const estadoPrev = String(estadoOriginal || '').toLowerCase();
      const vigenteORevision = ['emitido', 'revision'].includes(estadoPrev);
      const detalleCambio = detalleLineasPackingModificado(
        lineasInicialesRef.current,
        lineasFinal
      );

      if (emitirExcel && plId && vigenteORevision && !detalleCambio) {
        const getRes = await fetch(`${urlApiProd}/inventario/packinglists/${plId}`, {
          headers: authHeaders(),
        });
        const plActual = await getRes.json().catch(() => ({}));
        if (!getRes.ok) {
          throw new Error(plActual.error || 'No se pudo cargar el packing list para emitir Excel.');
        }
        const draftLabelsEditados =
          modoDraft === 'varios' ? [...draftsEditadosRef.current] : [];
        await emitirPackingListExcel(
          {
            ...plActual,
            con_ganancia: plActual.con_ganancia ?? conGanancia,
          },
          {
            conGanancia: !!(plActual.con_ganancia ?? conGanancia),
            draftLabels: draftLabelsEditados,
          }
        );
        draftsEditadosRef.current = new Set();
        if (redirigirTrasGuardar) {
          abrirRedirectPackingList();
          onSaved?.(plActual, { emitirExcel, redirecting: true });
        } else {
          onSaved?.(plActual, { emitirExcel });
          onHide();
        }
        return;
      }

      const crearNuevaVersion = !!plId && vigenteORevision && detalleCambio;

      let payload = {
        ...buildPayload(estado),
        estado,
        lineas: lineasFinal.map((ln, i) => ({ ...ln, orden: i + 1 })),
      };
      if (crearNuevaVersion) {
        // Backend asigna y mantiene sincronizados nombre y versión.
        payload = prepararPayloadNuevaVersion(payload, plId);
      }

      const url =
        crearNuevaVersion || !plId
          ? `${urlApiProd}/inventario/packinglists`
          : `${urlApiProd}/inventario/packinglists/${plId}`;
      const res = await fetch(url, {
        method: crearNuevaVersion || !plId ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Error al guardar');
      setPlId(data.id);
      setOrigenPlId(data.id_origen_pl ?? null);
      if (data.version != null) setVersion(Number(data.version) || 1);
      setEstadoOriginal(data.estado || estado);
      const lineasGuardadas =
        Array.isArray(data.lineas) && data.lineas.length ? data.lineas : lineasFinal;
      lineasInicialesRef.current = lineasGuardadas.map((ln) => ({ ...ln }));

      const plParaExcel = {
        ...data,
        estado: data.estado || estado,
        lineas: lineasGuardadas,
        con_ganancia: data.con_ganancia ?? conGanancia,
      };

      if (emitirExcel) {
        try {
          const draftLabelsEditados =
            packingExistente?.id && modoDraft === 'varios'
              ? [...draftsEditadosRef.current]
              : [];
          await emitirPackingListExcel(plParaExcel, {
            conGanancia: !!plParaExcel.con_ganancia,
            draftLabels: draftLabelsEditados,
          });
          draftsEditadosRef.current = new Set();
        } catch (excelErr) {
          // El packing ya quedó guardado como emitido; avisar solo el fallo del Excel
          setError(
            excelErr?.message || 'Se guardó el packing list, pero falló la descarga del Excel.'
          );
          onSaved?.(data, { emitirExcel: true });
          return;
        }
      }

      if (redirigirTrasGuardar) {
        abrirRedirectPackingList();
        onSaved?.(data, { emitirExcel, redirecting: true });
      } else {
        onSaved?.(data, { emitirExcel });
        onHide();
      }
    } catch (e) {
      setError(e.message || 'Error al guardar');
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  };

  return (
    <>
    <ElegantModal
      show={show && !redirectOpen}
      onClose={onHide}
      title={
        soloLectura
          ? 'Ver packing list (histórico)'
          : packingExistente?.id || plId
            ? 'Editar packing list'
            : 'Crear packing list'
      }
      subtitle={
        soloLectura
          ? 'Consulta del packing list archivado. No se pueden guardar cambios ni emitir una nueva versión.'
          : draftEdicionActivo
            ? `Solo se muestran y editan las líneas del Draft ${String(draftEdicionActivo).padStart(2, '0')}. Los demás drafts no se modifican desde esta vista.`
            : packingExistente?.id || plId
              ? 'Modifique los datos del packing list y el detalle de líneas.'
              : 'Complete los datos del packing list a partir de las notas fiscales seleccionadas.'
      }
      titleId="inventario-packinglist-title"
      size="full"
      dismissable={false}
      footer={
        loading ? null : soloLectura ? (
          <div className="d-flex justify-content-end w-100">
            <Button variant="secondary" type="button" onClick={onHide}>
              Cerrar
            </Button>
          </div>
        ) : (
          <div className="d-flex gap-2 justify-content-between flex-wrap w-100 align-items-center">
            <Button variant="secondary" type="button" onClick={onHide} disabled={saving}>
              Cancelar
            </Button>
            <div className="d-flex gap-2 flex-wrap align-items-center justify-content-end flex-grow-1">
              {error ? (
                <div
                  className="text-danger small text-end me-1 inv-pl-footer-error"
                  role="alert"
                  aria-live="polite"
                >
                  {error}
                </div>
              ) : null}
              <Button
                variant="outline-secondary"
                type="button"
                disabled={saving}
                onClick={() => persistir('borrador')}
              >
                {savingAction === 'borrador' ? 'Guardando…' : 'Guardar borrador'}
              </Button>
              <Button
                variant="primary"
                type="button"
                disabled={saving}
                onClick={() => persistir('emitido', { emitirExcel: true })}
              >
                {savingAction === 'excel' ? 'Guardando y descargando…' : 'Emitir Excel'}
              </Button>
            </div>
          </div>
        )
      }
    >
      {loading ? (
        <p className="text-center text-muted mb-0">
          <Spinner animation="border" size="sm" className="me-2" />
          Cargando datos…
        </p>
      ) : (
        <fieldset
          disabled={soloLectura}
          className="border-0 p-0 m-0"
          style={{ minWidth: 0 }}
        >
          {soloLectura ? (
            <Alert variant="warning" className="py-2 mb-3 small">
              Este packing list es histórico: puede revisar datos y líneas, pero no modificarlos.
            </Alert>
          ) : null}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group
                className="position-relative"
                style={piMenuOpen ? { zIndex: 30 } : undefined}
              >
                <Form.Label>Proforma Invoice (PI)</Form.Label>
                <div className="position-relative">
                  {!soloLectura ? (
                  <Form.Control
                    ref={piInputRef}
                    type="text"
                    autoComplete="off"
                    value={piBusqueda}
                    onChange={onPiBusquedaChange}
                    onFocus={() => setPiMenuOpen(true)}
                    onBlur={onPiInputBlur}
                    placeholder="Buscar y agregar PI (número o correlativo)…"
                  />
                  ) : null}
                  {!soloLectura && piMenuOpen && proformasFiltradas.length > 0 ? (
                    <ListGroup
                      className="position-absolute w-100 shadow border rounded overflow-auto bg-white"
                      style={{
                        zIndex: 40,
                        top: '100%',
                        marginTop: 2,
                        maxHeight: 220,
                      }}
                    >
                      {proformasFiltradas.map((pi) => (
                        <ListGroup.Item
                          key={pi.id_proforma}
                          action
                          className="py-1 px-2"
                          style={{ lineHeight: 1.25 }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            addProforma(pi);
                          }}
                        >
                          {proformaEtiqueta(pi, clientes)}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : null}
                </div>
                {idsProforma.length > 0 ? (
                  <div className="d-flex flex-wrap gap-1 mt-2">
                    {proformasSel.map((pi) => {
                      const lbl = proformaEtiqueta(pi, clientes);
                      return (
                        <Badge
                          key={pi.id_proforma}
                          bg="secondary"
                          className="d-inline-flex align-items-center gap-1 fw-normal py-1 px-2"
                        >
                          <span>{lbl}</span>
                          {!soloLectura ? (
                            <Button
                              type="button"
                              variant="link"
                              className="text-white text-decoration-none p-0 lh-1"
                              style={{ fontSize: '1rem', minWidth: '1rem' }}
                              onClick={() => removeProforma(pi.id_proforma)}
                              aria-label={`Quitar ${lbl}`}
                            >
                              ×
                            </Button>
                          ) : null}
                        </Badge>
                      );
                    })}
                  </div>
                ) : (
                  <Form.Text className="text-muted">Ninguna PI seleccionada</Form.Text>
                )}
                {balancesPi.length > 0 ? (
                  <div className="inv-pl-pi-balance mt-2">
                    <div className="inv-pl-pi-balance-head">
                      <span>Volumen PI</span>
                      {loadingSaldosPi ? <small>Cargando saldos...</small> : null}
                    </div>
                    <div className="inv-pl-pi-balance-list">
                      {balancesPi.map((b) => (
                        <div
                          key={b.id}
                          className={`inv-pl-pi-balance-row${b.excedido ? ' is-over' : ''}`}
                        >
                          <strong title={b.etiqueta}>{b.etiqueta}</strong>
                          <span>Total: {formatM3Display(b.contratado, '0,000')} m³</span>
                          <span>
                            Usado: {formatM3Display(b.utilizado, '0,000')} m³
                          </span>
                          <span>Este PL: {formatM3Display(b.actual, '0,000')} m³</span>
                          <span>
                            Saldo: {formatM3Display(b.saldoPosterior, '0,000')} m³
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Booking</Form.Label>
                <Form.Control
                  value={booking}
                  onChange={(e) => setBooking(e.target.value)}
                  placeholder="Nombre de la reserva"
                  autoComplete="off"
                  readOnly={soloLectura}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Nombre packing list</Form.Label>
                <Form.Control value={nombre} readOnly />
                <Form.Text className="text-muted">Se genera desde el booking y la versión.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Versión</Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  value={version > 1 ? `V${version}` : 'V1'}
                />
                <Form.Text className="text-muted">
                  Nueva versión solo si cambia el detalle de líneas al emitir Excel.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Label className="d-block">Tipo de packing list</Form.Label>
              <Form.Check
                type="checkbox"
                id="pl-con-ganancia"
                label="Con ganancia"
                checked={conGanancia}
                onChange={(e) => {
                  setConGanancia(e.target.checked);
                  if (e.target.checked) setSinGanancia(false);
                }}
              />
              <Form.Check
                type="checkbox"
                id="pl-sin-ganancia"
                label="Sin ganancia"
                checked={sinGanancia}
                onChange={(e) => {
                  setSinGanancia(e.target.checked);
                  if (e.target.checked) setConGanancia(false);
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="d-block">Draft</Form.Label>
              <Form.Check
                inline
                type="radio"
                name="modoDraft"
                id="draft-uno"
                label="Un draft"
                checked={modoDraft === 'uno'}
                disabled={!!draftEdicionActivo}
                onChange={() => setModoDraft('uno')}
              />
              <Form.Check
                inline
                type="radio"
                name="modoDraft"
                id="draft-varios"
                label="Más de uno"
                checked={modoDraft === 'varios'}
                disabled={!!draftEdicionActivo}
                onChange={() => setModoDraft('varios')}
              />
              {modoDraft === 'varios' ? (
                <Form.Control
                  type="number"
                  min={2}
                  max={20}
                  className="mt-2"
                  style={{ maxWidth: 100 }}
                  value={cantidadDrafts}
                  disabled={!!draftEdicionActivo}
                  onChange={(e) => setCantidadDrafts(e.target.value)}
                />
              ) : null}
            </Col>
          </Row>

          {!soloLectura ? (
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group
                className="position-relative"
                style={nfMenuOpen ? { zIndex: 30 } : undefined}
              >
                <Form.Label>Agregar otra nota fiscal</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    autoComplete="off"
                    placeholder="Buscar por NF, proveedor, grado o medida…"
                    value={nfBusqueda}
                    disabled={addingNf}
                    onChange={(e) => {
                      setNfBusqueda(e.target.value);
                      setNfMenuOpen(true);
                    }}
                    onFocus={() => setNfMenuOpen(true)}
                    onBlur={() => {
                      window.setTimeout(() => setNfMenuOpen(false), 180);
                    }}
                  />
                  {nfMenuOpen && nfsParaAgregar.length > 0 ? (
                    <ListGroup
                      className="position-absolute w-100 shadow border rounded overflow-auto bg-white"
                      style={{
                        zIndex: 40,
                        top: '100%',
                        marginTop: 2,
                        maxHeight: 220,
                      }}
                    >
                      {nfsParaAgregar.map((item) => (
                        <ListGroup.Item
                          key={`${item.id_inventario_nf}-${item.id_inventario_linea}`}
                          action
                          className="py-1 px-2"
                          style={{ lineHeight: 1.25 }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            agregarLineaNf(item);
                          }}
                        >
                          {item.label}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : null}
                </div>
                {nfMenuOpen && nfsParaAgregar.length === 0 ? (
                  <Form.Text className="text-muted">
                    No hay partes de NF disponibles para agregar
                  </Form.Text>
                ) : (
                  <Form.Text className="text-muted">
                    Seleccione cada detalle (línea) de la nota fiscal para agregarlo al packing list.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
          ) : null}

          <Alert variant="info" className="py-2 mb-2 small">
            La composición de la mercadería se modifica desde Inventario. En este Packing List puede ajustar la cantidad de paquetes.
          </Alert>
          <Form.Label className="mb-2">Detalle de líneas</Form.Label>
          <div className="inv-pl-table-wrap border rounded">
            <Table bordered hover size="sm" className="align-middle mb-0 inv-pl-table">
              <thead>
                <tr>
                  {modoDraft === 'varios' && !draftEdicionActivo ? (
                    <th className="inv-pl-col-draft">Draft</th>
                  ) : null}
                  <th>PI</th>
                  <th>Cont.</th>
                  <th>Proveedor</th>
                  <th>Grado</th>
                  <th>Cast. RW</th>
                  <th>Almacén</th>
                  <th>NF</th>
                  <th>Paq.</th>
                  <th>Pcs/paq</th>
                  <th>Tot. pcs</th>
                  <th>Esp.</th>
                  <th>Anc.</th>
                  <th>Lar.</th>
                  <th>M³</th>
                  <th>Peso</th>
                  <th>Producto</th>
                  <th className="inv-pl-col-usd-icb">{PL_COL_LABEL_USD_M3_ICB}</th>
                  <th>Total USD</th>
                  {conGanancia ? (
                    <>
                      <th>Esp.G</th>
                      <th>Anc.G</th>
                      <th>Lar.G</th>
                      <th>Cast.G</th>
                      <th>M³.G</th>
                      <th>Total USD G</th>
                    </>
                  ) : null}
                  {!soloLectura ? <th className="inv-pl-col-action" aria-label="Quitar NF" /> : null}
                </tr>
              </thead>
              <tbody>
                {lineasTabla.map(({ ln, idx }, pos) => {
                  const grupoCont = contenedorEnDraftKey(ln, drafts[0]);
                  const grupoPrev =
                    pos > 0
                      ? contenedorEnDraftKey(lineasTabla[pos - 1].ln, drafts[0])
                      : grupoCont;
                  const containerBreak = pos > 0 && grupoCont !== grupoPrev;
                  return (
                  <tr
                    key={`${ln.id_inventario_linea || idx}-${idx}`}
                    className={containerBreak ? 'inv-pl-container-break' : undefined}
                  >
                    {modoDraft === 'varios' && !draftEdicionActivo ? (
                      <td className="inv-pl-col-draft">
                        <Form.Select
                          size="sm"
                          className="inv-pl-select-draft"
                          value={normalizeDraftLabel(ln.draft_label) || drafts[0]}
                          onChange={(e) => patchLinea(idx, { draft_label: e.target.value })}
                        >
                          {drafts.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    ) : null}
                    <td>
                      <Form.Select
                        size="sm"
                        className="inv-pl-select"
                        value={ln.id_proforma || ''}
                        onChange={(e) =>
                          patchLinea(idx, {
                            id_proforma: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                      >
                        <option value="">—</option>
                        {proformasSel
                          .filter((pi) => piCompatibleConLinea(pi, ln, catalogoMedidasPi))
                          .map((pi) => (
                            <option key={pi.id_proforma} value={pi.id_proforma}>
                              {proformaEtiqueta(pi, clientes)}
                            </option>
                          ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min={1}
                        className="inv-pl-input-xs"
                        value={ln.correlativo_contenedor ?? 1}
                        onChange={(e) =>
                          patchLinea(idx, {
                            correlativo_contenedor: Math.max(1, Number(e.target.value) || 1),
                          })
                        }
                      />
                    </td>
                    <td>
                      <TruncCell className="inv-pl-trunc-prov">{ln.proveedor}</TruncCell>
                    </td>
                    <td>{ln.grado || '—'}</td>
                    <td className="text-end">{formatEspesorDisplay(ln.castigo_rw)}</td>
                    <td>
                      <TruncCell className="inv-pl-trunc-sm">{ln.almacen}</TruncCell>
                    </td>
                    <td>{ln.nro_nf ?? '—'}</td>
                    <td>
                      <Stepper
                        value={ln.paquetes}
                        min={0}
                        onChange={(v) => patchLinea(idx, { paquetes: v })}
                      />
                    </td>
                    <td>
                      <span>{formatMilesDisplay(ln.pcs_paq)}</span>
                    </td>
                    <td className="text-end">{formatMilesDisplay(ln.total_pcs)}</td>
                    <td className="text-end">{formatEspesorDisplay(ln.espesor)}</td>
                    <td className="text-end">{formatMilesDisplay(ln.ancho)}</td>
                    <td className="text-end">{formatMilesDisplay(ln.largo)}</td>
                    <td>
                      <FormattedNumberInput
                        className="inv-pl-input-sm text-end"
                        formatter={(v) => formatM3Display(v)}
                        placeholder="0,000"
                        value={ln.total_m3 ?? ''}
                        onChange={(v) => patchLinea(idx, { total_m3: v })}
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="inv-pl-input-sm text-end"
                        formatter={formatPesoDisplay}
                        placeholder="0"
                        value={ln.peso_mercaderia}
                        onChange={(v) => patchLinea(idx, { peso_mercaderia: v })}
                      />
                    </td>
                    <td>
                      <TruncCell className="inv-pl-trunc-prod">{ln.producto_concatenado}</TruncCell>
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="inv-pl-input-sm text-end"
                        formatter={(v) => formatMoneyDisplay(v).replace('—', '')}
                        placeholder="0,00"
                        value={ln.precio_usd_m3 ?? ''}
                        onChange={(v) => patchPrecioIcbPorGrado(idx, v)}
                      />
                    </td>
                    <td className="text-end text-nowrap">
                      {formatMoneyDisplay(ln.total_usd)}
                    </td>
                    {conGanancia ? (
                      <>
                        <td>
                          <FormattedNumberInput
                            className="inv-pl-input-xs text-end"
                            formatter={(v) => formatEspesorDisplay(v).replace('—', '')}
                            placeholder="0,0"
                            value={ln.espesor_ganancia ?? ln.espesor ?? ''}
                            onChange={(v) => patchLinea(idx, { espesor_ganancia: v })}
                          />
                        </td>
                        <td>
                          <FormattedNumberInput
                            className="inv-pl-input-xs text-end"
                            formatter={(v) => formatMilesDisplay(v, '')}
                            placeholder="0"
                            value={ln.ancho_ganancia ?? ln.ancho ?? ''}
                            onChange={(v) => patchLinea(idx, { ancho_ganancia: v })}
                          />
                        </td>
                        <td>
                          <FormattedNumberInput
                            className="inv-pl-input-xs text-end"
                            formatter={(v) => formatMilesDisplay(v, '')}
                            placeholder="0"
                            value={ln.largo_ganancia ?? ln.largo ?? ''}
                            onChange={(v) => patchLinea(idx, { largo_ganancia: v })}
                          />
                        </td>
                        <td>
                          <FormattedNumberInput
                            className="inv-pl-input-xs text-end"
                            formatter={(v) => formatEspesorDisplay(v).replace('—', '')}
                            placeholder="0,0"
                            value={ln.castigo_rw_ganancia ?? ln.castigo_rw ?? ''}
                            onChange={(v) => patchLinea(idx, { castigo_rw_ganancia: v })}
                          />
                        </td>
                        <td>
                          <FormattedNumberInput
                            className="inv-pl-input-sm text-end"
                            formatter={(v) => formatM3Display(v)}
                            placeholder="0,000"
                            value={
                              ln.total_m3_ganancia ?? ln.total_m3_original ?? ln.total_m3 ?? ''
                            }
                            onChange={(v) => patchLinea(idx, { total_m3_ganancia: v })}
                          />
                        </td>
                        <td className="text-end text-nowrap">
                          {formatMoneyDisplay(totalUsdGanancia(ln))}
                        </td>
                      </>
                    ) : null}
                    {!soloLectura ? (
                      <td className="inv-pl-col-action">
                        {ln.id_inventario_nf != null ? (
                          <button
                            type="button"
                            className="inv-pl-btn-remove"
                            title={`Quitar NF ${ln.nro_nf || ''}`}
                            aria-label={`Quitar nota fiscal ${ln.nro_nf || ''}`}
                            onClick={() => pedirEliminarNf(ln)}
                          >
                            <FaTimes size={11} />
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          {!lineas.length ? (
            <div className="text-muted text-center py-3">
              Sin líneas. Busque una nota fiscal y agréguela al packing list.
            </div>
          ) : null}
        </fieldset>
      )}
    </ElegantModal>

    <ElegantModal
      show={show && redirectOpen}
      onClose={cancelRedirect}
      title="Redireccionando"
      subtitle="Será enviado a la página de Packing List."
      titleId="inventario-packinglist-redirect"
      size="lg"
      footer={
        <div className="d-flex justify-content-end w-100">
          <Button variant="secondary" type="button" onClick={cancelRedirect}>
            Cancelar
          </Button>
        </div>
      }
    >
      <p className="mb-0 text-center">
        Redireccionando a Packing List en{' '}
        <strong>{redirectSec}</strong> segundo{redirectSec === 1 ? '' : 's'}…
      </p>
    </ElegantModal>

    <ElegantModal
      show={!!nfAEliminar}
      onClose={cancelarEliminarNf}
      title="Quitar línea del packing list"
      subtitle="Se quitará esta parte de la nota fiscal del packing list."
      titleId="inventario-packinglist-nf-eliminar"
      size="lg"
      footer={
        <div className="d-flex gap-2 justify-content-end flex-wrap w-100">
          <Button variant="secondary" type="button" onClick={cancelarEliminarNf}>
            Cancelar
          </Button>
          <Button variant="danger" type="button" onClick={confirmarEliminarNf}>
            Quitar
          </Button>
        </div>
      }
    >
      {nfAEliminar ? (
        <p className="mb-0">
          ¿Quitar de la NF <strong>{nfAEliminar.nro_nf}</strong>
          {nfAEliminar.grado || nfAEliminar.medida ? (
            <>
              {' '}
              ({[nfAEliminar.grado, nfAEliminar.medida].filter(Boolean).join(' · ')})
            </>
          ) : null}
          {nfAEliminar.proveedor ? (
            <>
              {' '}
              de <strong>{nfAEliminar.proveedor}</strong>
            </>
          ) : null}
          ?
        </p>
      ) : null}
    </ElegantModal>
    </>
  );
}
